import { fg } from '@confluence/feature-gating';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	onboardingStateGetOnboardingStateQuery,
	type onboardingStateGetOnboardingStateQuery$data,
	type onboardingStateGetOnboardingStateQuery$variables,
} from '@confluence/onboarding-helpers/entry-points/onboardingStateGetOnboardingStateQuery.ts';

import useConnectorOnboardingTeamTypeQuery, {
	type useConnectorOnboardingTeamTypeQuery$data,
	type useConnectorOnboardingTeamTypeQuery$variables,
} from './hooks/__generated__/useConnectorOnboardingTeamTypeQuery.graphql.ts';
import useConnectorStatesQuery, {
	type useConnectorStatesQuery$data,
	type useConnectorStatesQuery$variables,
} from './hooks/__generated__/useConnectorStatesQuery.graphql.ts';
import { isWithinEnrolmentWindow } from './utils/enrolmentWindow.ts';

// User should not be an admin when using this preload
export const preloadConnectorOnboardingState = async (userId?: string) => {
	if (!fg('twcg-1489-cc-3p-connector-onboarding-fg')) {
		return;
	}

	const { isRovoEnabled, orgId, cloudId } = getPreloaderFnContext();
	if (!isRovoEnabled) {
		return;
	}

	// Always preload onboarding state first. It is lightweight and tells us
	// whether the more expensive AGG queries are still needed.
	const onboardingResult = await query<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>({
		query: onboardingStateGetOnboardingStateQuery,
		variables: {
			key: Object.values(TWCG_1489_CONNECTOR_ONBOARDING),
		},
	}).catch(() => null);

	// Extract the enrolment date and skip the AGG queries when the experiment
	// is past its active window (30 days).
	const enrolmentDateStr = onboardingResult?.data?.onboardingState?.find(
		(entry) => entry.key === TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE,
	)?.value;

	if (!isWithinEnrolmentWindow(enrolmentDateStr)) {
		return;
	}

	// When the user has dismissed both promo surfaces, all visibility flags in
	// useConnectorPromoConfig will be false, so the AGG queries are not needed.
	const promoDismissed = onboardingResult?.data?.onboardingState?.find(
		(entry) => entry.key === TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED,
	)?.value;

	if (promoDismissed === 'both') {
		return;
	}

	// Within the window — fire the AGG queries in parallel.
	const tasks: Array<Promise<unknown>> = [];

	if (userId) {
		tasks.push(
			queryAgg<
				useConnectorOnboardingTeamTypeQuery$data,
				useConnectorOnboardingTeamTypeQuery$variables
			>({
				query: useConnectorOnboardingTeamTypeQuery,
				variables: { userId },
			}),
		);
	}

	if (orgId && cloudId) {
		tasks.push(
			queryAgg<useConnectorStatesQuery$data, useConnectorStatesQuery$variables>({
				query: useConnectorStatesQuery,
				variables: { orgId, resourceId: cloudId },
			}),
		);
	}

	if (tasks.length > 0) {
		await waitForAllReturnWhenError(tasks);
	}
};
